import * as React from "react";
import { CPLN_HELM_RELEASE_PREFIX, homeLink, linksOf, request } from "../../../services/cpln";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { decodeRevisionFrom, fetchReleaseItems, getMarketplaceErrorMessage } from "../utils";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { BasePathContext, useBasePath } from "../../../reactContexts/basePathContext";
import { MarketplaceReleaseInstance, ReleaseRevision } from "../types/release";
import { ReleaseInfo } from "./info";
import { ReleaseUpgrade } from "./upgrade";
import { ReleaseRevisions } from "./revisions";
import { ReleaseDrift, computeDriftItems } from "./drift";
import { Loader } from "../../../components/layout/loader";
import { Secret } from "../../../schema/types/secret/entity";
import { IS_DEPLOYMENT_ENV_PROD } from "../../../envVariables";
import { Template } from "../types/template";
import { Alert } from "antd";
import { useAppSelector } from "../../../store/hooks";
import { selectOrg } from "../../../store/contextSlice";
import { TemplateReleaseDetailHeader } from "../../workload/templateReleaseHeader";
import { ContentLayout } from "../../../components/layout/contentLayout";
import { SingleNavMenu } from "../../../components/antd/layout/singleNavMenu";
import { SingleNavMenuLink, toLinkPage } from "../../identity/useNavMenu";

type RouteParams = "release";
export const MarketplaceReleaseDetail: React.FC = () => {
  const org = useAppSelector(selectOrg);

  const { release: releaseName } = useParams<RouteParams>();

  const basePathUpdated = useBasePath(`/marketplace/release/${releaseName}/*`);

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [template, setTemplate] = React.useState<Template>(null as any);
  const [release, setRelease] = React.useState<MarketplaceReleaseInstance>(null as any);

  const [items, setItems] = React.useState<any[]>([]);
  const [itemsNotFound, setItemsNotFound] = React.useState<any[]>([]);
  const [revisions, setRevisions] = React.useState<ReleaseRevision[]>([]);
  const [revisionStateSecretName, setRevisionStateSecretName] = React.useState("");

  React.useEffect(() => {
    fetchRelease();
  }, []);

  React.useEffect(() => {
    if (!release) {
      return;
    }

    fetchTemplate();
  }, [release]);

  React.useEffect(() => {
    if (!release) {
      return;
    }

    if (revisions.length < 1) {
      return;
    }

    fetchItems();
  }, [revisions]);

  React.useEffect(() => {
    if (!release) {
      return;
    }

    // TODO Get agent health
    // TODO Get volumeset info
  }, [items]);

  async function fetchRelease() {
    try {
      setIsLoading(true);

      let _items: Secret[] = [];
      let nextLink: string | undefined = undefined;

      const url = homeLink("secret") + "/-query";
      const body = {
        kind: "secret",
        spec: {
          match: "all",
          terms: [
            { op: "~", property: "name", value: `${CPLN_HELM_RELEASE_PREFIX}` },
            { op: "exists", tag: "cpln/marketplace" },
            { op: "=", tag: "name", value: `${releaseName}` },
          ],
        },
      };
      const { data } = await request({ method: "post", url: url, body: body });
      _items = data.items;
      nextLink = linksOf(data).next;

      while (nextLink) {
        const { data } = await request({ url: nextLink, body: body });
        nextLink = linksOf(data).next;
        _items = _items.concat(data.items);
      }
      const secrets = _items
        .filter((i) => i.name.startsWith(`${CPLN_HELM_RELEASE_PREFIX}`))
        .filter((i) => (i.tags || {}).name === releaseName);
      const promises: Promise<any>[] = secrets.map((s) => {
        const selfLink = linksOf(s).self!;
        return request({ url: selfLink + "/-reveal" });
      });
      const revealedSecrets: Secret[] = (await Promise.allSettled(promises))
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value.data);

      const revisions = revealedSecrets.map(decodeRevisionFrom).filter(Boolean) as ReleaseRevision[];
      setRevisions(revisions);

      const sortedRevisions = revisions.toSorted((a, b) => {
        if (a.version > b.version) return -1;
        if (b.version > a.version) return 1;
        return 0;
      });
      const firstRevision = sortedRevisions[sortedRevisions.length - 1];
      const lastRevision = sortedRevisions[0];

      const lastRevisionIndex = Math.max(
        revisions.findIndex((r) => r.version === lastRevision.version),
        0,
      );
      const lastRevisionSecret = secrets[lastRevisionIndex];
      const templateName = (secrets[0]?.tags || {})["cpln/marketplace-template"];
      const templateVersion = (lastRevisionSecret?.tags || {})["cpln/marketplace-template-version"];

      setRevisionStateSecretName(revealedSecrets[lastRevisionIndex]?.name || "");

      setRelease({
        kind: "marketplace-release",
        lastModified: lastRevision.info.lastDeployed,
        created: firstRevision.info.firstDeployed,
        // where do I use this?
        tags: {},
        gvc: lastRevision.gvc || "",
        org: org,
        name: releaseName!,
        id: releaseName!,
        template: templateName,
        version: templateVersion,
        revision: lastRevision.version,
      });
      setIsLoading(false);
    } catch (e) {
      const errorMessage = getMarketplaceErrorMessage(e);
      setError(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
      setIsLoading(false);
    }
  }

  async function fetchTemplate() {
    try {
      const { data } = await request<Template>({ service: "marketplace", url: `/template/${release.template}` });
      setTemplate(data);
    } catch (e) {
      const errorMessage = getMarketplaceErrorMessage(e);
      setError(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
      console.error(`Marketplace App Detail Info Fetch Template`, errorMessage);
    }
  }

  async function fetchItems() {
    try {
      const revision = revisions.toSorted((a, b) => {
        if (a.version > b.version) return -1;
        if (b.version > a.version) return 1;
        return 0;
      })[0];
      const _itemResults = await fetchReleaseItems(revision.info.resources);
      const _itemsNotFound = _itemResults.filter((_i) => _i.notFound === true);
      const _items = _itemResults.filter((_i) => !_i.notFound);

      const kindOrder = [
        "gvc",
        "workload",
        "volumeset",
        "identity",
        "domain",
        "agent",
        "secret",
        "cloudaccount",
        "group",
        "serviceaccount",
        "policy",
        "auditctx",
      ];

      let _sortedItems: any[] = [];
      for (const kind of kindOrder) {
        _sortedItems = _sortedItems.concat(..._items.filter((i) => i.kind === kind));
      }
      setItems(_sortedItems);
      let _sortedItemsNotFound: any[] = [];
      for (const kind of kindOrder) {
        _sortedItemsNotFound = _sortedItemsNotFound.concat(..._itemsNotFound.filter((i) => i.kind === kind));
      }
      setItemsNotFound(_sortedItemsNotFound);
      setIsLoading(false);
    } catch (e) {
      const errorMessage = getMarketplaceErrorMessage(e);
      setError(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
      setIsLoading(false);
    }
  }

  const usedGvcs: string[] = [];
  for (const item of items) {
    const { isValid, name: gvcName } = parseLink(linksOf(item).gvc || "", { useInputCtx: true });
    if (isValid && !usedGvcs.includes(gvcName)) {
      usedGvcs.push(gvcName);
    }
  }

  // NOTE: These useMemos must be placed before any early returns to maintain hook order
  const driftCount = React.useMemo(() => {
    const driftItems = computeDriftItems(revisions, items, itemsNotFound);
    return driftItems.filter((d) => d.hasDrift).length;
  }, [revisions, items, itemsNotFound]);

  const links: SingleNavMenuLink[] = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [
      toLinkPage("Info", "/info"),
      toLinkPage("Drift", "/drift", {
        renderIcon:
          driftCount > 0
            ? () => (
                <span
                  style={{
                    backgroundColor: "var(--color-danger)",
                    color: "white",
                    borderRadius: "50%",
                    width: 18,
                    height: 18,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    marginLeft: 6,
                  }}
                >
                  {driftCount}
                </span>
              )
            : undefined,
      }),
      toLinkPage("Upgrade", "/upgrade"),
      toLinkPage("Revisions", "/revisions"),
    ];
    return links;
  }, [driftCount]);

  if (error) {
    return (
      <>
        <Alert type={"error"} message="Error" description={`${error}`} />
        {IS_DEPLOYMENT_ENV_PROD ? null : (
          <div className="flex flex-col gap-4 text-sm">
            <div>Release</div>
            <pre>{JSON.stringify(release, null, 2)}</pre>
            <div>Revisions</div>
            <pre>{JSON.stringify(revisions, null, 2)}</pre>
          </div>
        )}
      </>
    );
  }

  if (!release || !template || isLoading) {
    return <Loader />;
  }

  return (
    <>
      <BasePathContext.Provider value={basePathUpdated}>
        <TemplateReleaseDetailHeader release={release} items={items} />
        <ContentLayout
          nav={<SingleNavMenu useDetailHeader={true} links={links} />}
          content={
            <Routes>
              <Route index element={<Navigate to={`${basePathUpdated}/info`} replace />} />
              <Route
                path={`info/*`}
                element={
                  <ReleaseInfo
                    template={template}
                    release={release}
                    usedGvcs={usedGvcs}
                    items={items}
                    itemsNotFound={itemsNotFound}
                  />
                }
              />
              <Route
                path={`drift/*`}
                element={
                  <ReleaseDrift
                    revisions={revisions}
                    items={items}
                    itemsNotFound={itemsNotFound}
                  />
                }
              />
              <Route
                path={`upgrade/*`}
                element={
                  <ReleaseUpgrade
                    template={template}
                    release={release}
                    currentRevisionSecretName={revisionStateSecretName}
                    onRefresh={fetchRelease}
                  />
                }
              />
              <Route
                path={`revisions/*`}
                element={<ReleaseRevisions release={release} revisions={revisions} onRefresh={fetchRelease} />}
              />
            </Routes>
          }
        />
      </BasePathContext.Provider>
    </>
  );
};
